import React, { createContext, useContext, useState, useCallback } from 'react';

const NLPCacheContext = createContext();

export const useNLPCache = () => {
  const context = useContext(NLPCacheContext);
  if (!context) {
    throw new Error('useNLPCache must be used within NLPCacheProvider');
  }
  return context;
};

export const NLPCacheProvider = ({ children }) => {
  // Global cache for all NLP results across all tabs
  const [nlpCache, setNLPCache] = useState({
    // Structure: { deals: { tfidf, ner, topics }, companies: { ... }, investors: { ... } }
  });

  // Track loading states for each endpoint
  const [loadingStates, setLoadingStates] = useState({
    deals: { tfidf: false, ner: false, topics: false },
    companies: { tfidf: false, ner: false, topics: false },
    investors: { tfidf: false, ner: false, topics: false }
  });

  // Get cached data for a specific tab and type
  const getCached = useCallback((tab, type) => {
    return nlpCache[tab]?.[type] || null;
  }, [nlpCache]);

  // Set cached data for a specific tab and type
  const setCached = useCallback((tab, type, data) => {
    setNLPCache(prev => ({
      ...prev,
      [tab]: {
        ...prev[tab],
        [type]: data
      }
    }));
  }, []);

  // Update loading state for a specific tab and type
  const setLoading = useCallback((tab, type, isLoading) => {
    setLoadingStates(prev => ({
      ...prev,
      [tab]: {
        ...prev[tab],
        [type]: isLoading
      }
    }));
  }, []);

  // Check if a specific tab has all NLP data loaded
  const isTabComplete = useCallback((tab) => {
    return !!(nlpCache[tab]?.tfidf && nlpCache[tab]?.ner && nlpCache[tab]?.topics);
  }, [nlpCache]);

  // Get total progress across all tabs
  const getTotalProgress = useCallback(() => {
    const tabs = ['deals', 'companies', 'investors'];
    let completed = 0;
    let total = tabs.length * 3; // 3 types per tab

    tabs.forEach(tab => {
      if (nlpCache[tab]?.tfidf) completed++;
      if (nlpCache[tab]?.ner) completed++;
      if (nlpCache[tab]?.topics) completed++;
    });

    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  }, [nlpCache]);

  // Check if any tab is currently loading
  const isAnyLoading = useCallback(() => {
    const tabs = ['deals', 'companies', 'investors'];
    const types = ['tfidf', 'ner', 'topics'];

    return tabs.some(tab =>
      types.some(type => loadingStates[tab]?.[type])
    );
  }, [loadingStates]);

  const value = {
    nlpCache,
    loadingStates,
    getCached,
    setCached,
    setLoading,
    isTabComplete,
    getTotalProgress,
    isAnyLoading
  };

  return (
    <NLPCacheContext.Provider value={value}>
      {children}
    </NLPCacheContext.Provider>
  );
};
